import React, { useState } from 'react';
import classnames from 'classnames';
import './swatch.scss';
import {
  bool, func, oneOf, shape, string, number
} from 'prop-types';
import { Image } from '@thd-olt-component-react/core-ui';
import { Tooltip } from '@thd-olt-component-react/tooltip';
import { useImpression } from '@thd-olt-component-react/impression';
import { TooltipContent } from '../tooltip-content/tooltip-content';
import { SWATCH_SIZE } from '../../statics';

export const Swatch = (props) => {
  const {
    channel,
    displayInToolTip,
    hoveredCombinationDetails,
    isAvailable,
    isSelected,
    isTooltipsEnabled,
    miniSwatch,
    onClick,
    onMouseEnter,
    onMouseLeave,
    swatchGuid,
    size,
    value,
    index,
    swatchItemId,
    componentImpressionData,
    isImpressionsRequired,
    addMargin
  } = props;
  const [isHovered, setIsHovered] = useState(false);
  const [isClickedMobile, setIsClickedMobile] = useState(false);
  const IMAGE_SIZE = size || (miniSwatch ? SWATCH_SIZE.XS : SWATCH_SIZE.SM);
  const isMobile = channel === 'mobile';

  const shouldShowTooltip = isHovered
    && isTooltipsEnabled && hoveredCombinationDetails?.combinationDetails && !miniSwatch;
  const title = shouldShowTooltip ? '' : value;

  const swatchClasses = classnames('super-sku__inline-swatch border-radius--medium', {
    'super-sku__inline-swatch--selected': isSelected,
    'super-sku__inline-swatch--disabled': !isSelected && !isAvailable && !miniSwatch,
    'super-sku__inline-swatch__mini-swatch': miniSwatch,
    'super-sku__inline-swatch__display-in-tooltip': displayInToolTip,
    'super-sku__inline-swatch__space': addMargin
  });

  const imageClasses = classnames('border-radius--medium', {
    'mini-image': miniSwatch,
    'mini-image--selected': displayInToolTip && isSelected
  });

  const swatchBorderClass = classnames('swatch__image', 'border-radius--medium', {
    'swatch__image--lg': size === SWATCH_SIZE.LG,
    'swatch__image--md': size === SWATCH_SIZE.MD
  });

  const mouseEnter = (event) => {
    if (!isMobile) {
      setIsHovered(true);
      onMouseEnter(event);
    }
  };
  const mouseLeave = (event) => {
    if (!isMobile) {
      setIsHovered(false);
      onMouseLeave(event);
    }
  };

  const impressionProviderContextData = useImpression({
    data: {
      id: swatchItemId || '',
      name: componentImpressionData?.name || '',
      component: componentImpressionData?.component || '',
      position: componentImpressionData?.position || 1,
      type: componentImpressionData?.type || '',
      category: 'ssku'
    }
  });

  const onClickMobile = () => {
    if (isMobile) {
      setIsClickedMobile(true);
    }
  };

  const swatchChild = (
    <button
      className={swatchClasses}
      onClick={(event) => {
        onClick(event);
        onClickMobile();
      }}
      onMouseEnter={mouseEnter}
      onMouseLeave={mouseLeave}
      type="button"
    >
      <div
        className={swatchBorderClass}
        ref={
          index !== 0
            && isImpressionsRequired
            && (isHovered || isClickedMobile)
            ? impressionProviderContextData.ref
            : null
        }
        // eslint-disable-next-line react/no-unknown-property
        clickid={
          index !== 0
            && isImpressionsRequired
            && (isHovered || isClickedMobile)
            ? impressionProviderContextData.clickID
            : null
        }
      >
        <Image
          alt={value}
          className={imageClasses}
          src={swatchGuid}
          title={title}
          width={IMAGE_SIZE}
          height={IMAGE_SIZE}
          lazy
        />
      </div>
      {displayInToolTip && <div className="swatch__text">{title}</div>}
    </button>
  );

  return (shouldShowTooltip
    ? (
      <Tooltip
        channel={channel}
        content={<TooltipContent hoveredCombinationDetails={hoveredCombinationDetails} />}
        interactive
      >
        {swatchChild}
      </Tooltip>
    )
    : (swatchChild)
  );
};

Swatch.displayName = 'Swatch';

Swatch.propTypes = {
  channel: oneOf(['desktop', 'mobile']).isRequired,
  displayInToolTip: bool,
  hoveredCombinationDetails: shape({}),
  isAvailable: bool.isRequired,
  isSelected: bool.isRequired,
  isTooltipsEnabled: bool,
  miniSwatch: bool,
  onClick: func.isRequired,
  onMouseEnter: func.isRequired,
  onMouseLeave: func.isRequired,
  size: string,
  swatchGuid: string.isRequired,
  value: string.isRequired,
  swatchItemId: string,
  index: number,
  componentImpressionData: shape({
    name: string,
    component: string,
    position: number,
    type: string,
  }),
  isImpressionsRequired: bool,
  addMargin: bool
};

Swatch.defaultProps = {
  displayInToolTip: false,
  hoveredCombinationDetails: {},
  miniSwatch: false,
  isTooltipsEnabled: false,
  size: null,
  swatchItemId: null,
  index: 0,
  componentImpressionData: {
    name: '',
    component: '',
    position: 1,
    type: '',
  },
  isImpressionsRequired: false,
  addMargin: true
};
